import React, { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import DropdownCard, { type DropdownCardProps } from '../DropdownCard';
import DropdownCardContent from '../DropdownCardContent';
import { Action, type ActionProps } from './Action';
import { DropdownMenuContext } from './DropdownMenuContext';
import { StyledContainer, StyledSpinner, TriggerContainer } from './Styles';

export interface RenderTriggerProps {
  isTriggered: boolean;
  toggleTrigger: () => void;
}

export interface Props {
  additionalClickOutsideRefs?: RefObject<HTMLElement>[];
  children: ((props: RenderTriggerProps) => React.ReactNode) | React.ReactNode;
  className?: string;
  forwardCardRef?: RefObject<HTMLDivElement>;
  /** @deprecated */
  isLarge?: boolean;
  renderTrigger: (props: RenderTriggerProps) => React.ReactNode;
  loading?: boolean;
  triggerFullWidth?: boolean;
  placement?: DropdownCardProps['placement'];
  matchTriggerWidth?: boolean;
  /**
   * In some cases, e.g. when a dropdown container has a modal in it, we need to render
   * the content of the dropdown permanently, regardless of whether it's triggered or not.
   * Please avoid using this prop unless absolutely necessary.
   */
  prerenderContent?: boolean;
}

const Container: React.FC<Props> = ({
  additionalClickOutsideRefs = [],
  children,
  className,
  forwardCardRef,
  renderTrigger,
  triggerFullWidth,
  loading,
  placement,
  matchTriggerWidth,
  prerenderContent = false,
}) => {
  const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);
  const localCardRef = useRef<HTMLDivElement>(null);
  const cardRef = forwardCardRef || localCardRef;
  const [isTriggered, setIsTriggered] = useState(false);
  const toggleTrigger = () => setIsTriggered(!isTriggered);
  const closeModal = () => setIsTriggered(false);

  useEffect(() => {
    if (isTriggered && !loading && !children) {
      closeModal();
    }
  }, [children, isTriggered, loading]);

  return (
    <DropdownMenuContext.Provider
      value={useMemo(() => ({ closeModal, isTriggered }), [isTriggered])}
    >
      <div className={className} data-dropdown-menu-container>
        <StyledContainer>
          <TriggerContainer ref={setTriggerRef} fullWidth={triggerFullWidth}>
            {renderTrigger({ isTriggered, toggleTrigger })}
          </TriggerContainer>
          {(isTriggered || prerenderContent) && (
            <DropdownCard
              data-cy-dropdown-menu
              ref={cardRef}
              triggerRef={triggerRef}
              isOpen={isTriggered}
              appear={isTriggered}
              onClose={closeModal}
              additionalRefs={additionalClickOutsideRefs}
              placement={placement}
              matchTriggerWidth={matchTriggerWidth}
            >
              <DropdownCardContent>
                {loading && <StyledSpinner />}
                {!loading &&
                  (typeof children === 'function'
                    ? children({ isTriggered, toggleTrigger })
                    : children)}
              </DropdownCardContent>
            </DropdownCard>
          )}
        </StyledContainer>
      </div>
    </DropdownMenuContext.Provider>
  );
};

Container.displayName = 'DropdownMenu.Container';
Action.displayName = 'DropdownMenu.Action';

export { type ActionProps, DropdownMenuContext };

export default {
  Container,
  Action,
};
